import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format, differenceInDays } from "date-fns";
import {
  Calendar as CalIcon,
  Users,
  Search,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Loader2,
  BellRing,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { INTENTS } from "@/data/site";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intentToRoom = {
  meet_people: "8_bed_dorm",
  scenic: "double_mountain_view",
  private: "king_mountain_view",
};

export default function BookingEngine({ id = "booking" }) {
  const [intent, setIntent] = useState("scenic");
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [guests, setGuests] = useState("2");

  const [step, setStep] = useState("search"); // search | results | guest | confirmed
  const [searching, setSearching] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [mode, setMode] = useState("book"); // book | waitlist

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [waitlisted, setWaitlisted] = useState(null);

  const prefillRef = useRef(null);
  const nights = checkIn && checkOut ? Math.max(1, differenceInDays(checkOut, checkIn)) : 0;

  // Room cards can request a specific room type — remember it and highlight after search
  useEffect(() => {
    const onPrefill = (e) => {
      const rt = e?.detail?.roomType;
      if (rt) {
        prefillRef.current = rt;
        setStep("search");
        setConfirmation(null);
      }
    };
    window.addEventListener("prefill-booking", onPrefill);
    return () => window.removeEventListener("prefill-booking", onPrefill);
  }, []);

  const sortedRooms = useMemo(() => {
    if (!availability) return [];
    const rooms = [...availability.rooms];
    const pref = prefillRef.current;
    return rooms.sort((a, b) => {
      if (pref && (a.id === pref) !== (b.id === pref)) return a.id === pref ? -1 : 1;
      if (a.fits_guests !== b.fits_guests) return a.fits_guests ? -1 : 1;
      if (intent === "meet_people" && a.category !== b.category)
        return a.category === "social" ? -1 : 1;
      if (intent !== "meet_people" && a.category !== b.category)
        return a.category === "private" ? -1 : 1;
      return a.price - b.price;
    });
  }, [availability, intent]);

  const checkAvailability = async () => {
    if (!checkIn || !checkOut) return toast.error("Pick check-in and check-out dates");
    if (checkOut <= checkIn) return toast.error("Check-out must be after check-in");
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/availability`, {
        params: {
          check_in: format(checkIn, "yyyy-MM-dd"),
          check_out: format(checkOut, "yyyy-MM-dd"),
          guests: parseInt(guests, 10),
        },
      });
      setAvailability(data);
      setStep("results");
      if (!data.any_available) toast("No rooms fit these dates — try shifting by a day");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check availability");
    } finally {
      setSearching(false);
    }
  };

  const startBooking = (room, bookingMode = "book") => {
    setSelectedRoom(room);
    setMode(bookingMode);
    setStep("guest");
  };

  const submit = async () => {
    if (!name.trim() || !phone.trim()) return toast.error("Add your name and contact number");
    setSubmitting(true);
    const base = {
      name,
      phone,
      email: email || null,
      check_in: format(checkIn, "yyyy-MM-dd"),
      check_out: format(checkOut, "yyyy-MM-dd"),
      guests: parseInt(guests, 10),
      room_type: selectedRoom.id,
    };
    try {
      if (mode === "waitlist") {
        await axios.post(`${API}/waitlist`, base);
        setWaitlisted({ roomName: selectedRoom.name });
        setStep("confirmed");
      } else {
        const { data } = await axios.post(`${API}/bookings`, { ...base, intent });
        setConfirmation({
          ...data,
          nights,
          estTotal: selectedRoom.total_price,
          roomLabel: selectedRoom.name,
        });
        setStep("confirmed");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit. Try again.";
      toast.error(typeof msg === "string" ? msg : "Booking failed");
      if (e?.response?.status === 409) {
        setStep("results");
        checkAvailability();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep("search");
    setConfirmation(null);
    setWaitlisted(null);
    setAvailability(null);
    setSelectedRoom(null);
    setCheckIn(undefined);
    setCheckOut(undefined);
    setName("");
    setPhone("");
    setEmail("");
    prefillRef.current = null;
  };

  return (
    <section
      id={id}
      data-testid="booking-engine"
      className="relative z-20 -mt-24 md:-mt-32 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="glass-strong rounded-3xl p-5 sm:p-7 md:p-9 grain relative overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {step === "confirmed" ? (
              waitlisted ? (
                <WaitlistDone key="wl" roomName={waitlisted.roomName} onReset={resetAll} />
              ) : (
                <ConfirmationView key="confirm" booking={confirmation} onReset={resetAll} />
              )
            ) : step === "guest" ? (
              <GuestForm
                key="guest"
                mode={mode}
                room={selectedRoom}
                nights={nights}
                guests={guests}
                checkIn={checkIn}
                checkOut={checkOut}
                name={name}
                phone={phone}
                email={email}
                setName={setName}
                setPhone={setPhone}
                setEmail={setEmail}
                submitting={submitting}
                onBack={() => setStep("results")}
                onSubmit={submit}
              />
            ) : (
              <motion.div
                key="search"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[#f5f0ea] flex items-center gap-2 font-semibold">
                      <Sparkles size={14} /> Live availability · instant confirmation
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-2 text-white">
                      When are you coming to Gangtok?
                    </h2>
                  </div>
                  <div className="text-xs text-neutral-400">
                    Best-price guarantee · Free cancellation up to 48h
                  </div>
                </div>

                {/* Intent pills */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6" data-testid="intent-filters">
                  {INTENTS.map((it) => {
                    const active = intent === it.id;
                    return (
                      <button
                        key={it.id}
                        data-testid={`intent-filter-${it.id}`}
                        onClick={() => {
                          setIntent(it.id);
                          prefillRef.current = intentToRoom[it.id] || prefillRef.current;
                        }}
                        className={`group relative rounded-2xl px-3 py-3 sm:py-4 text-left border transition-all duration-300 ${
                          active
                            ? "border-[#A7D38B] bg-[#62794d]/20 text-white"
                            : "border-white/10 bg-white/[0.03] text-neutral-300 hover:border-white/25"
                        }`}
                      >
                        <div className="text-xl">{it.emoji}</div>
                        <div className="mt-1 font-semibold text-sm">{it.label}</div>
                        <div className="text-[11px] text-neutral-400">{it.hint}</div>
                        {active && (
                          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#A7D38B] shadow-[0_0_10px_#A7D38B]" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Search bar */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 items-end">
                  <Field label="Check-in" icon={<CalIcon size={14} />} className="md:col-span-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid="check-in-btn"
                          className="w-full text-left rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/25 px-3 py-2.5 text-sm transition text-neutral-100"
                        >
                          {checkIn ? (
                            format(checkIn, "dd MMM yyyy")
                          ) : (
                            <span className="text-neutral-500">Pick a date</span>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="p-0 w-auto bg-neutral-900 border-white/10"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={checkIn}
                          onSelect={(d) => {
                            setCheckIn(d);
                            setAvailability(null);
                          }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>

                  <Field label="Check-out" icon={<CalIcon size={14} />} className="md:col-span-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid="check-out-btn"
                          className="w-full text-left rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/25 px-3 py-2.5 text-sm transition text-neutral-100"
                        >
                          {checkOut ? (
                            format(checkOut, "dd MMM yyyy")
                          ) : (
                            <span className="text-neutral-500">Pick a date</span>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="p-0 w-auto bg-neutral-900 border-white/10"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={checkOut}
                          onSelect={(d) => {
                            setCheckOut(d);
                            setAvailability(null);
                          }}
                          disabled={(d) =>
                            d < new Date(new Date().setHours(0, 0, 0, 0)) ||
                            (checkIn && d <= checkIn)
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>

                  <Field label="Guests" icon={<Users size={14} />} className="md:col-span-3">
                    <Select
                      value={guests}
                      onValueChange={(v) => {
                        setGuests(v);
                        setAvailability(null);
                      }}
                    >
                      <SelectTrigger
                        data-testid="guests-select"
                        className="bg-white/[0.04] border-white/10 hover:border-white/25 rounded-xl text-sm text-neutral-100"
                      >
                        <SelectValue placeholder="2" />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-900 border-white/10">
                        {[1, 2, 3, 4, 5, 6, 8].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n} {n === 1 ? "guest" : "guests"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <div className="md:col-span-3">
                    <button
                      onClick={checkAvailability}
                      disabled={searching}
                      data-testid="check-availability-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#62794d] text-[#f5f0ea] font-semibold px-6 py-3 text-sm hover:bg-[#7a9261] active:scale-[0.98] transition disabled:opacity-60 shadow-[0_10px_30px_rgba(98,121,77,0.4)]"
                    >
                      {searching ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Checking…
                        </>
                      ) : (
                        <>
                          <Search size={16} /> Check Availability
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Results */}
                <AnimatePresence>
                  {step === "results" && availability && (
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="mt-7 border-t border-white/10 pt-6"
                      data-testid="availability-results"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                        <h3 className="font-display text-lg md:text-lg font-semibold text-white">
                          {availability.any_available
                            ? "Available for your dates"
                            : "Nothing open for these dates"}
                        </h3>
                        <div className="text-[11px] text-neutral-400">
                          {format(checkIn, "dd MMM")} → {format(checkOut, "dd MMM")} ·{" "}
                          {availability.nights} night{availability.nights !== 1 ? "s" : ""} ·{" "}
                          {availability.guests} guest{availability.guests !== 1 ? "s" : ""}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {sortedRooms.map((room, i) => (
                          <ResultRow
                            key={room.id}
                            room={room}
                            index={i}
                            nights={availability.nights}
                            onBook={() => startBooking(room, "book")}
                            onNotify={() => startBooking(room, "waitlist")}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-neutral-400">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A7D38B] animate-pulse" />
                    5 people viewing tonight
                  </span>
                  <span>Free cancellation up to 48h before</span>
                  <span>No prepayment required</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

function ResultRow({ room, index, nights, onBook, onNotify }) {
  const unavailable = !room.fits_guests;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index, duration: 0.45 }}
      data-testid={`availability-row-${room.id}`}
      className={`flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border px-4 py-4 transition-all duration-300 ${
        unavailable
          ? "border-white/5 bg-white/[0.015] opacity-55"
          : "border-white/10 bg-white/[0.03] hover:border-[#A7D38B]/40"
      }`}
    >
      <div
        className="w-full sm:w-24 h-20 sm:h-16 rounded-xl bg-cover bg-center shrink-0"
        style={{ backgroundImage: `url(${room.image})` }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-white text-sm">{room.name}</span>
          <span
            data-testid={`availability-status-${room.id}`}
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
              room.sold_out
                ? "border-red-400/30 text-red-300 bg-red-500/10"
                : unavailable
                ? "border-amber-400/30 text-amber-300 bg-amber-500/10"
                : room.units_available <= 2
                ? "border-amber-400/30 text-amber-300 bg-amber-500/10"
                : "border-[#A7D38B]/30 text-[#A7D38B] bg-[#62794d]/10"
            }`}
          >
            {room.status_label}
          </span>
        </div>
        <div className="text-[11px] text-neutral-400 mt-1 truncate">
          {room.tag} · {room.best_for}
        </div>
      </div>
      <div className="sm:text-right shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          ₹{room.price.toLocaleString()}/{room.price_unit} · night
        </div>
        <div className="font-display text-xl font-bold text-white">
          {room.total_price ? `₹${room.total_price.toLocaleString()}` : "—"}
        </div>
        <div className="text-[10px] text-neutral-500">
          total · {nights} night{nights !== 1 ? "s" : ""}
        </div>
      </div>
      <div className="shrink-0">
        {unavailable ? (
          <button
            onClick={onNotify}
            data-testid={`notify-btn-${room.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-5 py-2.5 text-xs font-semibold text-neutral-200 hover:bg-white/10 transition"
          >
            <BellRing size={13} /> Notify me
          </button>
        ) : (
          <button
            onClick={onBook}
            data-testid={`book-now-${room.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] px-6 py-2.5 text-xs font-semibold hover:bg-[#7a9261] active:scale-[0.98] transition"
          >
            Book Now <ArrowRight size={13} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

function GuestForm({
  mode,
  room,
  nights,
  guests,
  checkIn,
  checkOut,
  name,
  phone,
  email,
  setName,
  setPhone,
  setEmail,
  submitting,
  onBack,
  onSubmit,
}) {
  const isWaitlist = mode === "waitlist";
  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid="guest-details-step"
    >
      <button
        onClick={onBack}
        data-testid="back-to-results-btn"
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-400 hover:text-white transition mb-5"
      >
        <ArrowLeft size={13} /> Back to availability
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <h3 className="font-display text-2xl font-semibold text-white">
            {isWaitlist ? "Join the waitlist" : "Almost there — your details"}
          </h3>
          <p className="text-xs text-neutral-400 mt-1">
            {isWaitlist
              ? "We'll ping you the moment this room opens up for your dates."
              : "No prepayment. Pay at check-in. Free cancellation up to 48h before."}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-5">
            <Field label="Your name" className="sm:col-span-1">
              <input
                data-testid="booking-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Aanya M."
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/25 focus:border-[#A7D38B] focus:outline-none px-3 py-2.5 text-sm transition text-neutral-100 placeholder:text-neutral-500"
              />
            </Field>
            <Field label="Phone" className="sm:col-span-1">
              <input
                data-testid="booking-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98xxxxxx21"
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/25 focus:border-[#A7D38B] focus:outline-none px-3 py-2.5 text-sm transition text-neutral-100 placeholder:text-neutral-500"
              />
            </Field>
            <Field label="Email" className="sm:col-span-2">
              <input
                data-testid="booking-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@example.com"
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/25 focus:border-[#A7D38B] focus:outline-none px-3 py-2.5 text-sm transition text-neutral-100 placeholder:text-neutral-500"
              />
            </Field>
          </div>

          <button
            onClick={onSubmit}
            disabled={submitting}
            data-testid="booking-submit"
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] font-semibold px-8 py-3.5 text-sm hover:bg-[#7a9261] active:scale-[0.98] transition disabled:opacity-60 shadow-[0_10px_30px_rgba(98,121,77,0.4)]"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {isWaitlist ? "Adding…" : "Confirming…"}
              </>
            ) : (
              <>
                {isWaitlist ? "Join Waitlist" : "Confirm Booking"} <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="booking-summary">
            <div
              className="w-full h-28 rounded-xl bg-cover bg-center mb-3"
              style={{ backgroundImage: `url(${room?.image})` }}
            />
            <div className="font-semibold text-white text-sm">{room?.name}</div>
            <div className="text-[11px] text-neutral-400">{room?.status_label}</div>
            <div className="mt-3 space-y-1.5 text-xs text-neutral-300">
              <Row k="Dates">
                {checkIn && format(checkIn, "dd MMM")} → {checkOut && format(checkOut, "dd MMM")}
              </Row>
              <Row k="Nights">{nights}</Row>
              <Row k="Guests">{guests}</Row>
              <Row k="Rate">
                ₹{room?.price?.toLocaleString()}/{room?.price_unit}
              </Row>
            </div>
            {!isWaitlist && (
              <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Est. total
                </span>
                <span className="font-display text-2xl font-bold text-white">
                  {room?.total_price ? `₹${room.total_price.toLocaleString()}` : "—"}
                </span>
              </div>
            )}
            <p className="text-[10px] text-neutral-500 mt-2">Taxes included · Pay at check-in</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Row({ k, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-neutral-500">{k}</span>
      <span className="text-white">{children}</span>
    </div>
  );
}

function Field({ label, icon, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 mb-1.5 flex items-center gap-1.5 font-semibold">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

function WaitlistDone({ roomName, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center max-w-lg mx-auto py-6"
      data-testid="waitlist-confirmation"
    >
      <div className="inline-flex w-16 h-16 rounded-full bg-white/[0.06] border border-white/10 items-center justify-center mb-4">
        <BellRing size={28} className="text-[#A7D38B]" />
      </div>
      <h3 className="font-display text-3xl font-bold tracking-tighter text-white">
        You're on the waitlist
      </h3>
      <p className="text-neutral-300 mt-2 text-sm">
        If a {roomName} frees up for your dates, you're the first to know.
      </p>
      <button
        onClick={onReset}
        data-testid="waitlist-reset-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] px-6 py-3 text-xs font-semibold hover:bg-[#7a9261] transition"
      >
        Search other dates
      </button>
    </motion.div>
  );
}

function ConfirmationView({ booking, onReset }) {
  const ref = booking?.id?.slice(0, 8).toUpperCase() || "—";
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(ref).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <motion.div
      key="confirm-view"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid="booking-confirmation"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#62794d]/25 blur-3xl rounded-full" />
      <div className="relative text-center max-w-xl mx-auto py-4">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 220 }}
          className="inline-flex w-16 h-16 rounded-full bg-[#62794d] items-center justify-center mb-4"
        >
          <CheckCircle2 size={32} className="text-[#f5f0ea]" />
        </motion.div>
        <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-white">
          Booking confirmed
        </h3>
        <p className="text-neutral-300 mt-2 text-sm">
          A pre-arrival email is on its way. See you in Gangtok.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
          <InfoRow label="Reference">
            <button
              onClick={copy}
              data-testid="copy-reference-btn"
              className="inline-flex items-center gap-2 text-[#A7D38B] font-mono text-sm hover:underline"
            >
              {ref}
              <Copy size={12} />
              {copied && <span className="text-[10px] text-neutral-400">copied</span>}
            </button>
          </InfoRow>
          <InfoRow label="Guest">{booking?.name}</InfoRow>
          <InfoRow label="Room">{booking?.roomLabel || booking?.room_type}</InfoRow>
          <InfoRow label="Stay">
            {booking?.check_in} → {booking?.check_out}
            {booking?.nights ? ` · ${booking.nights}n` : ""}
          </InfoRow>
          <InfoRow label="Guests">{booking?.guests}</InfoRow>
          <InfoRow label="Est. total">
            {booking?.estTotal ? `₹${booking.estTotal.toLocaleString()}` : "—"}
          </InfoRow>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onReset}
            data-testid="booking-another-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-5 py-2.5 text-xs font-semibold text-white hover:bg-white/10 transition"
          >
            Make another booking
          </button>
          <a
            href="#rooms"
            data-testid="explore-rooms-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] px-5 py-2.5 text-xs font-semibold hover:bg-[#7a9261] transition"
          >
            Explore more rooms
          </a>
        </div>

        <p className="text-[11px] text-neutral-500 mt-5">
          Pay on arrival · Free cancellation up to 48h before check-in · Reference ID: {ref}
        </p>
      </div>
    </motion.div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/8 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-sm text-white mt-0.5">{children}</div>
    </div>
  );
}
